import { useState, FC } from 'react';
import { observer } from 'mobx-react';
import { Button, Checkbox, Select, Upload, message } from 'antd';
import { CloudUploadOutlined } from '@ant-design/icons';
import { useMenu, useThirdApp, useUpdateThirdAppParams } from '@/store';
import UnitItem from '@/components/UnitItem';
import AppImg from '@/components/AppImg';
import APP_ITEMS from '../LayoutBox/items/apps';
import { THIRD_APP_ICON_CACHE } from '@/utils/THIRD_APP_ICON_CACHE';
import { $fm, $t } from '@/Intl';
import * as API from '@/api';

import './index.less';

const getBuiltinClassNames = (builtinList: BuiltinApp[]): string[] => {
  const appItems = Object.values(APP_ITEMS);
  const res = builtinList.map(({ apkName }) => {
    const matched = appItems.find((item) => item.apkName === apkName);
    if (matched) {
      return matched.className;
    }
    return '';
  });
  return res.filter((s) => s);
};

interface ThirdAppProps {
  canUploadThirdApp: () => boolean;
  afterUpload?: (appInfo: ThirdAppInfo) => void;
}

const ThirdApp: FC<ThirdAppProps> = observer(
  ({ canUploadThirdApp, afterUpload }) => {
    const [uploading, setUploading] = useState(false);
    const [menu, updateMenu] = useMenu();
    const updateThirdAppParam = useUpdateThirdAppParams();
    const [tmpApps, setTmpApps] = useThirdApp();

    const { thirdApp = [], builtinApp = [] } = menu;

    const handleBeforeUpload = (file: File) => {
      if (!canUploadThirdApp()) {
        return false;
      }

      if (!/\.apk$/.test(file.name)) {
        message.error($t('UPLOAD_APK_ERROR'));
        return false;
      }

      /**
       * 去重
       */
      if (
        thirdApp.some((app) => app.apkName === file.name) ||
        builtinApp.some((app) => app.apkName === file.name)
      ) {
        message.error($t('REAPTAPP_WARN'));
        return false;
      }

      handleAnalysis(file);

      return false;
    };

    const handleAnalysis = async (tmpApp: File) => {
      if (!tmpApp) {
        return message.error($t('UPLOAD_APK_ERROR'));
      }
      setUploading(true);

      try {
        const res = await API.apkAnalysis(tmpApp);
        setUploading(false);
        /**
         * 去重
         */
        if (
          thirdApp.some((app) => app.packageName === res.packageName) ||
          getBuiltinClassNames(builtinApp).some(
            (classname) => classname === res.className,
          )
        ) {
          message.error($t('REAPTAPP_WARN'));
          return false;
        }
        const payload: ThirdAppInfo = {
          displayName: res.displayName,
          apkName: tmpApp.name,
          packageName: res.packageName,
          className: res.className,
          boot: 0,
          autoLaunchWithoutOperate: 0,
          intervalWithoutOperate: 0,
          showIcon: 0,
          hide: 0,
          icon: 'icon/third_party.png',
          ownerClass: '',
          classNames: res.classNames || [],
        };

        /**
         * 当前可能存在上传app className 为空的情况，此时会造成三方app图标缓存映射问题，包括三方app列表，以及layout内的三方app图标
         * 所以处理为, 无className时，使用默认图标，同时桌面不添加该app，不支持布局（本地桌面实际也不支持）
         */
        let icon = !res.className ? 'icon/third_party.png' : res.icon;
        THIRD_APP_ICON_CACHE[res.className] = icon;

        onFinish(payload, tmpApp);
      } catch (e) {
        message.error($t('APK_ERROR'));
      }
    };

    const onFinish = (appInfo?: ThirdAppInfo, app?: File) => {
      if (appInfo && app) {
        updateMenu({
          ...menu,
          thirdApp: [...thirdApp, appInfo],
        });
        setTmpApps([...tmpApps, app]);
        afterUpload?.(appInfo);
        message.success($t('UPLOAD_SUCCESS'));
      }
    };

    /**
     * ownerClass 存储值即为 "[包名]/[类名]", 还原纯类名仅用于导入历史 cust 时
     * (无 classNames 列表) 补充选项的展示文案
     */
    const ownerClassToClassName = (app: ThirdAppInfo) => {
      const v = app.ownerClass || '';
      const prefix = app.packageName + '/';
      return v.startsWith(prefix) ? v.slice(prefix.length) : v;
    };

    /**
     * Device Owner Class 下拉选项:
     * value 直接格式化为 "[包名]/[类名]", 与 ownerClass 存储值一致, 选中即下发, 无需二次拼接
     * label 展示纯类名, 便于阅读
     */
    const getOwnerClassOptions = (app: ThirdAppInfo) => {
      const pkg = app.packageName;
      const list = app.classNames || [];
      const options = list.map((name) => ({
        label: name,
        value: `${pkg}/${name}`,
      }));
      // 导入历史 cust 时无 classNames 列表, 将当前已选值补进选项以保证回显
      const cur = app.ownerClass || '';
      if (cur && !options.some((o) => o.value === cur)) {
        options.push({ label: ownerClassToClassName(app), value: cur });
      }
      // 无(空值)选项置顶, 选择后 ownerClass 为空, 生成的 xml 中不携带该属性
      return [{ label: $t('OWNER_CLASS_NONE'), value: '' }, ...options];
    };

    return (
      <>
        <UnitItem label={$fm('ADD_THIRD_APP')}>
          <Upload
            disabled={uploading}
            showUploadList={false}
            accept='.apk'
            beforeUpload={handleBeforeUpload}
          >
            <Button
              icon={<CloudUploadOutlined />}
              type='primary'
              loading={uploading}
            >
              {' '}
              {$fm('UPLOAD')}
            </Button>
          </Upload>
        </UnitItem>
        {thirdApp.map((app, i) => {
          return (
            <div className='third-app-item' key={i}>
              <UnitItem label='' className='third-app-title'>
                <AppImg src={THIRD_APP_ICON_CACHE[app.className]} width={28} />
                &nbsp;&nbsp;{app.displayName}
              </UnitItem>
              <UnitItem label={$fm('BOOT_TYPE')}>
                <Select
                  value={app.boot}
                  onChange={(v) => {
                    updateThirdAppParam(i, 'boot', v);
                  }}
                >
                  <Select.Option value={0}>{$fm('NOBOOT')}</Select.Option>
                  <Select.Option value={1}>{$fm('BOOT')}</Select.Option>
                  <Select.Option value={2}>{$fm('BOOT_NETWORK')}</Select.Option>
                </Select>
              </UnitItem>
              {app.boot !== 1 ? null : (
                <>
                  <UnitItem label={$fm('APP_AUTOLAUNCH')}>
                    <Checkbox
                      checked={!!app.autoLaunchWithoutOperate}
                      onChange={(e) => {
                        updateThirdAppParam(
                          i,
                          'autoLaunchWithoutOperate',
                          Number(e.target.checked),
                        );
                      }}
                    />
                  </UnitItem>
                  {app.autoLaunchWithoutOperate === 0 ? null : (
                    <>
                      <UnitItem label={$fm('INTERVAL_WITHOUT_OPERATE')}>
                        <Select
                          value={app.intervalWithoutOperate}
                          onChange={(v) => {
                            updateThirdAppParam(i, 'intervalWithoutOperate', v);
                          }}
                        >
                          <Select.Option value={5}>
                            {$fm('SECONDS', { n: 5 })}
                          </Select.Option>
                          <Select.Option value={10}>
                            {$fm('SECONDS', { n: 10 })}
                          </Select.Option>
                          <Select.Option value={15}>
                            {$fm('SECONDS', { n: 15 })}
                          </Select.Option>
                          <Select.Option value={30}>
                            {$fm('SECONDS', { n: 30 })}
                          </Select.Option>
                          <Select.Option value={45}>
                            {$fm('SECONDS', { n: 45 })}
                          </Select.Option>
                          <Select.Option value={60}>
                            {$fm('SECONDS', { n: 60 })}
                          </Select.Option>
                        </Select>
                      </UnitItem>
                      <UnitItem label={$fm('SHOW_ICON')}>
                        <Checkbox
                          checked={!!app.showIcon}
                          onChange={(e) => {
                            updateThirdAppParam(
                              i,
                              'showIcon',
                              Number(e.target.checked),
                            );
                          }}
                        />
                      </UnitItem>
                    </>
                  )}
                </>
              )}
              <UnitItem label={$fm('DEVICE_OWNER_CLASS')}>
                <Select
                  showSearch
                  dropdownMatchSelectWidth={false}
                  optionFilterProp='children'
                  filterOption={(input, option) =>
                    String(option?.children ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  value={app.ownerClass || ''}
                  onChange={(v: string) => {
                    updateThirdAppParam(i, 'ownerClass', v);
                  }}
                >
                  {getOwnerClassOptions(app).map((opt) => (
                    <Select.Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Option>
                  ))}
                </Select>
              </UnitItem>
            </div>
          );
        })}
      </>
    );
  },
);

export default ThirdApp;
